import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cx } from '@/lib/cx';
import styles from './Field.module.css';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'> {
  label: string;
  /** Shown under the control. Becomes the error text when `error` is set. */
  hint?: string;
  error?: string;
  /**
   * A control inside the input, at its trailing edge — a password reveal, a unit,
   * a clear. It belongs here rather than in the caller because only this
   * component knows where the input actually is: a caller positioning against
   * the field would be measuring against the label and message too.
   */
  trailing?: ReactNode;
}

/**
 * A labelled input.
 *
 * The id is generated and wired to the label here, so a caller cannot forget it —
 * an unlabelled input is invisible to a screen reader and its click target stops
 * at the box. `aria-describedby` points at whichever message is showing, and
 * `aria-invalid` states the error rather than leaving a red border to imply it.
 */
export const Field = ({ label, hint, error, required, trailing, ...rest }: FieldProps) => {
  const id = useId();
  const messageId = `${id}-message`;
  const message = error ?? hint;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
      </label>

      <div className={styles.control}>
        <input
          id={id}
          className={cx(styles.input, error && styles.invalid, trailing && styles.hasTrailing)}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          required={required}
          {...rest}
        />
        {trailing && <span className={styles.trailing}>{trailing}</span>}
      </div>

      {message && (
        <span id={messageId} className={cx(styles.message, error && styles.error)}>
          {message}
        </span>
      )}
    </div>
  );
};
